import React, { useCallback, useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  ContactRulerOverview,
  ContactRulerPreview,
  ContactRulerReturnDueItem,
  ContactRulerStep,
  ContactRulerStepKey,
  downloadReturnDueCsv,
  getBirthdayPreview,
  getContactRuler,
  getLoyaltyNearRewardPreview,
  getProtocolCarePreview,
  getReturnDue,
  getReturnPreview,
  LoyaltyNearRewardPreview,
  patchContactRuler,
  triggerBrowserDownload,
} from "../../../services/contactRulerService";
import { PaginationMeta } from "../../../types/api";

const RETURN_DUE_PER_PAGE = 10;

/** Espelha `MarketingAbVariant::forClientId` (client_id % 2). */
const abVariantForClient = (clientId: number): "A" | "B" =>
  clientId % 2 === 1 ? "B" : "A";

const toggleFieldForStep = (
  key: ContactRulerStepKey
):
  | "birthday_greetings_enabled"
  | "appointment_reviews_enabled"
  | "protocol_care_reminders_enabled"
  | "return_reminders_enabled"
  | "reactivation_campaigns_enabled" => {
  switch (key) {
    case "birthday":
      return "birthday_greetings_enabled";
    case "post_visit":
      return "appointment_reviews_enabled";
    case "protocol_care":
      return "protocol_care_reminders_enabled";
    case "return":
      return "return_reminders_enabled";
    case "reactivation":
      return "reactivation_campaigns_enabled";
  }
};

const stepCardClassName = (step: ContactRulerStep): string => {
  if (!step.enabled) {
    return "h-100 shadow-none border";
  }
  if (!step.quota_ok) {
    return "h-100 shadow-none border border-warning";
  }
  return "h-100 shadow-none border border-success";
};

const ContactRulerPage = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [overview, setOverview] = useState<ContactRulerOverview | null>(null);
  const [birthdayLeadDays, setBirthdayLeadDays] = useState(0);
  const [returnLeadDays, setReturnLeadDays] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [birthdayPreview, setBirthdayPreview] = useState<ContactRulerPreview | null>(null);
  const [returnPreview, setReturnPreview] = useState<ContactRulerPreview | null>(null);
  const [protocolCarePreview, setProtocolCarePreview] = useState<ContactRulerPreview | null>(null);
  const [loyaltyNearRewardPreview, setLoyaltyNearRewardPreview] =
    useState<LoyaltyNearRewardPreview | null>(null);
  const [loadingBirthdayPreview, setLoadingBirthdayPreview] = useState(false);
  const [loadingReturnPreview, setLoadingReturnPreview] = useState(false);
  const [loadingProtocolCarePreview, setLoadingProtocolCarePreview] = useState(false);
  const [loadingLoyaltyNearRewardPreview, setLoadingLoyaltyNearRewardPreview] =
    useState(false);
  const [dueItems, setDueItems] = useState<ContactRulerReturnDueItem[]>([]);
  const [dueMeta, setDueMeta] = useState<PaginationMeta | null>(null);
  const [duePage, setDuePage] = useState(1);
  const [loadingDue, setLoadingDue] = useState(false);
  const [exportingDue, setExportingDue] = useState(false);

  const applyOverview = useCallback((data: ContactRulerOverview) => {
    setOverview(data);
    const birthday = data.steps.find((step) => step.key === "birthday");
    const lead = Number(birthday?.summary?.lead_days ?? 0);
    setBirthdayLeadDays(Number.isFinite(lead) ? lead : 0);
    const returnStep = data.steps.find((step) => step.key === "return");
    const returnLead = Number(returnStep?.summary?.lead_days ?? 0);
    setReturnLeadDays(Number.isFinite(returnLead) ? returnLead : 0);
  }, []);

  const loadBirthdayPreview = useCallback(async () => {
    setLoadingBirthdayPreview(true);
    try {
      const response = await getBirthdayPreview();
      setBirthdayPreview(response.data);
    } catch {
      setBirthdayPreview(null);
    } finally {
      setLoadingBirthdayPreview(false);
    }
  }, []);

  const loadReturnPreview = useCallback(async () => {
    setLoadingReturnPreview(true);
    try {
      const response = await getReturnPreview();
      setReturnPreview(response.data);
    } catch {
      setReturnPreview(null);
    } finally {
      setLoadingReturnPreview(false);
    }
  }, []);

  const loadProtocolCarePreview = useCallback(async () => {
    setLoadingProtocolCarePreview(true);
    try {
      const response = await getProtocolCarePreview();
      setProtocolCarePreview(response.data);
    } catch {
      setProtocolCarePreview(null);
    } finally {
      setLoadingProtocolCarePreview(false);
    }
  }, []);

  const loadLoyaltyNearRewardPreview = useCallback(async () => {
    setLoadingLoyaltyNearRewardPreview(true);
    try {
      const response = await getLoyaltyNearRewardPreview();
      setLoyaltyNearRewardPreview(response.data);
    } catch {
      setLoyaltyNearRewardPreview(null);
    } finally {
      setLoadingLoyaltyNearRewardPreview(false);
    }
  }, []);

  const loadDue = useCallback(async (page: number) => {
    setLoadingDue(true);
    try {
      const response = await getReturnDue({
        page,
        per_page: RETURN_DUE_PER_PAGE,
        days: 7,
      });
      setDueItems(response.data);
      setDueMeta(response.meta);
      setDuePage(response.meta.current_page);
    } catch {
      setDueItems([]);
      setDueMeta(null);
      toast.error(t("marketing.contactRuler.returnDue.loadError"));
    } finally {
      setLoadingDue(false);
    }
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await getContactRuler();
      applyOverview(response.data);
    } catch {
      setLoadError(true);
      toast.error(t("marketing.contactRuler.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [applyOverview, t]);

  useEffect(() => {
    document.title = t("marketing.contactRuler.pageTitle");
    void load();
    void loadBirthdayPreview();
    void loadReturnPreview();
    void loadProtocolCarePreview();
    void loadLoyaltyNearRewardPreview();
    void loadDue(1);
  }, [
    load,
    loadBirthdayPreview,
    loadReturnPreview,
    loadProtocolCarePreview,
    loadLoyaltyNearRewardPreview,
    loadDue,
    t,
  ]);

  const onToggle = async (step: ContactRulerStep) => {
    const field = toggleFieldForStep(step.key);
    setSavingKey(step.key);
    try {
      const response = await patchContactRuler({
        [field]: !step.enabled,
      });
      applyOverview(response.data);
      toast.success(t("marketing.contactRuler.toast.saveSuccess"));
    } catch {
      toast.error(t("marketing.contactRuler.toast.saveError"));
    } finally {
      setSavingKey(null);
    }
  };

  const onSaveBirthdayLead = async () => {
    setSavingKey("birthday_lead");
    try {
      const response = await patchContactRuler({
        birthday_lead_days: birthdayLeadDays,
      });
      applyOverview(response.data);
      toast.success(t("marketing.contactRuler.toast.saveSuccess"));
    } catch {
      toast.error(t("marketing.contactRuler.toast.saveError"));
    } finally {
      setSavingKey(null);
    }
  };

  const onSaveReturnLead = async () => {
    setSavingKey("return_lead");
    try {
      const response = await patchContactRuler({
        return_reminder_lead_days: returnLeadDays,
      });
      applyOverview(response.data);
      toast.success(t("marketing.contactRuler.toast.saveSuccess"));
      void loadDue(1);
    } catch {
      toast.error(t("marketing.contactRuler.toast.saveError"));
    } finally {
      setSavingKey(null);
    }
  };

  const onExportDue = async () => {
    setExportingDue(true);
    try {
      const blob = await downloadReturnDueCsv(7);
      triggerBrowserDownload(blob, `retornos-a-vencer-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success(t("marketing.contactRuler.returnDue.exportSuccess"));
    } catch {
      toast.error(t("marketing.contactRuler.returnDue.exportError"));
    } finally {
      setExportingDue(false);
    }
  };

  const formatDueDate = (value: string): string => {
    const date = new Date(`${value}T12:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat(i18n.language, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const summaryLine = (step: ContactRulerStep): string => {
    switch (step.key) {
      case "birthday":
        return t("marketing.contactRuler.summary.birthday", {
          count: Number(step.summary.clients_with_birth_date ?? 0),
          week: Number(step.summary.birthdays_next_7_days ?? 0),
          days: Number(step.summary.lead_days ?? 0),
        });
      case "post_visit":
        return t("marketing.contactRuler.summary.postVisit", {
          hours: Number(step.summary.delay_hours ?? 2),
        });
      case "protocol_care": {
        const sentCount = Number(step.summary.sent_last_7_days ?? 0);
        const sent =
          sentCount > 0
            ? t("marketing.contactRuler.summary.protocolCareSent", { n: sentCount })
            : "";
        return t("marketing.contactRuler.summary.protocolCare", {
          days: Number(step.summary.offset_days ?? 1),
          protocols: Number(step.summary.active_protocols ?? 0),
          sent,
        });
      }
      case "return": {
        const sentCount = Number(step.summary.sent_last_7_days ?? 0);
        const sent =
          sentCount > 0
            ? t("marketing.contactRuler.summary.returnSent", { n: sentCount })
            : "";
        return t("marketing.contactRuler.summary.return", {
          count: Number(step.summary.services_with_return_on ?? 0),
          due: Number(step.summary.due_next_7_days ?? 0),
          sent,
        });
      }
      case "reactivation":
        return t("marketing.contactRuler.summary.reactivation", {
          count: Number(step.summary.eligible_count ?? 0),
          used: Number(step.summary.campaign_used ?? 0),
          quota: Number(step.summary.campaign_quota ?? 0),
        });
      default:
        return "";
    }
  };

  const statusBadge = (step: ContactRulerStep) => {
    if (!step.enabled) {
      return (
        <Badge color="secondary">{t("marketing.contactRuler.status.off")}</Badge>
      );
    }
    if (!step.quota_ok) {
      return (
        <Badge color="warning">
          {t("marketing.contactRuler.status.onNoQuota")}
        </Badge>
      );
    }
    return (
      <Badge color="success">{t("marketing.contactRuler.status.onReady")}</Badge>
    );
  };

  const orderedSteps = overview?.steps ?? [];
  const protocolCareStep = orderedSteps.find((step) => step.key === "protocol_care");
  const activeProtocolsCount = Number(protocolCareStep?.summary.active_protocols ?? 0);

  const persistStepOrder = async (next: ContactRulerStep[]) => {
    if (!overview) {
      return;
    }

    const nextOrder = next.map((step) => step.key);
    setOverview({
      ...overview,
      steps: next,
    });
    setSavingKey("step_order");

    try {
      const response = await patchContactRuler({
        contact_ruler_step_order: nextOrder,
      });
      applyOverview(response.data);
      toast.success(t("marketing.contactRuler.toast.saveSuccess"));
    } catch {
      await load();
      toast.error(t("marketing.contactRuler.toast.saveError"));
    } finally {
      setSavingKey(null);
    }
  };

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination || !overview) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const next = orderedSteps.slice();
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    await persistStepOrder(next);
  };

  const moveStep = async (fromIndex: number, direction: -1 | 1) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= orderedSteps.length) {
      return;
    }

    const next = orderedSteps.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    await persistStepOrder(next);
  };

  const quota = overview?.plan_limits.email_campaign_monthly_quota ?? 0;
  const used = overview?.plan_limits.campaign_used ?? 0;
  const birthdayStep = orderedSteps.find((step) => step.key === "birthday");
  const missingBirthDates =
    Number(birthdayStep?.summary.clients_without_birth_date ?? 0) > 0 ||
    Number(birthdayStep?.summary.clients_with_birth_date ?? 0) === 0;
  const withoutBirthCount = Number(
    birthdayStep?.summary.clients_without_birth_date ?? 0
  );
  const dueTotalPages = dueMeta?.last_page ?? 1;

  return (
    <div className="page-content">
      <ToastContainer />
      <Container fluid>
        <BreadCrumb
          title={t("marketing.contactRuler.breadcrumb")}
          pageTitle={t("marketing.contactRuler.pageTitle")}
        />

        <Row className="mb-3">
          <Col lg={12}>
            <p className="text-muted mb-2">{t("marketing.contactRuler.intro")}</p>
            {overview?.operational_chip && (
              <Badge color="light" className="text-muted border">
                {t("marketing.contactRuler.operationalChip")}{" "}
                <Link to={overview.operational_chip.config_path}>
                  {t("marketing.contactRuler.operationalChipLink")}
                </Link>
              </Badge>
            )}
          </Col>
        </Row>

        {overview && (
          <Alert color={quota <= 0 ? "warning" : "info"} className="mb-4">
            {quota <= 0
              ? t("marketing.contactRuler.quota.freeUpsell")
              : t("marketing.contactRuler.quota.usage", { used, quota })}
          </Alert>
        )}

        {loading && (
          <div className="text-center py-5">
            <Spinner />
          </div>
        )}

        {!loading && loadError && (
          <Alert color="danger">
            {t("marketing.contactRuler.empty.loadError")}{" "}
            <Button color="link" className="p-0 align-baseline" onClick={() => void load()}>
              {t("marketing.contactRuler.empty.retry")}
            </Button>
          </Alert>
        )}

        {!loading && !loadError && orderedSteps.length === 0 && (
          <Alert color="secondary">{t("marketing.contactRuler.empty.none")}</Alert>
        )}

        {!loading && !loadError && (
          <>
            <p className="text-muted small mb-2">
              {t("marketing.contactRuler.dragHint")}
            </p>
            <DragDropContext onDragEnd={(result) => void onDragEnd(result)}>
              <Droppable droppableId="contact-ruler-steps" direction="horizontal">
                {(droppableProvided) => (
                  <div
                    className="row g-3 contact-ruler-timeline"
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                  >
                    {orderedSteps.map((step, index) => (
                      <Draggable
                        key={step.key}
                        draggableId={step.key}
                        index={index}
                        isDragDisabled={savingKey === "step_order"}
                      >
                        {(draggableProvided) => (
                          <div
                            className="col-12 col-md-6 col-xl"
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                          >
                            <Card className={stepCardClassName(step)}>
                              <CardBody>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <p
                                      className="text-muted text-uppercase fs-11 mb-1"
                                      {...draggableProvided.dragHandleProps}
                                      aria-label={t("marketing.contactRuler.dragHandle")}
                                      style={{ cursor: "grab" }}
                                    >
                                      <i className="ri-drag-move-2-line me-1" />
                                      {t("marketing.contactRuler.stepLabel", {
                                        n: index + 1,
                                      })}
                                    </p>
                                    <h5 className="mb-0">
                                      {t(
                                        `marketing.contactRuler.steps.${step.key}.title`
                                      )}
                                    </h5>
                                  </div>
                                  <div className="d-flex flex-column align-items-end gap-1">
                                    {statusBadge(step)}
                                    <div className="btn-group btn-group-sm">
                                      <Button
                                        color="light"
                                        size="sm"
                                        className="px-1"
                                        disabled={
                                          index === 0 || savingKey === "step_order"
                                        }
                                        aria-label={t(
                                          "marketing.contactRuler.moveLeft"
                                        )}
                                        onClick={() => void moveStep(index, -1)}
                                      >
                                        <i className="ri-arrow-left-s-line" />
                                      </Button>
                                      <Button
                                        color="light"
                                        size="sm"
                                        className="px-1"
                                        disabled={
                                          index === orderedSteps.length - 1 ||
                                          savingKey === "step_order"
                                        }
                                        aria-label={t(
                                          "marketing.contactRuler.moveRight"
                                        )}
                                        onClick={() => void moveStep(index, 1)}
                                      >
                                        <i className="ri-arrow-right-s-line" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>

                      <p className="text-muted small mb-2">
                        {t(`marketing.contactRuler.steps.${step.key}.description`)}
                      </p>

                      <p className="small mb-2">
                        <span className="text-muted">
                          {t("marketing.contactRuler.channelLabel")}:{" "}
                        </span>
                        {t(`marketing.contactRuler.channel.${step.channel}`)}
                        {Boolean(step.summary.ab_enabled) && (
                          <Badge color="light" className="ms-2 text-muted border">
                            {t("marketing.contactRuler.abBadge")}
                          </Badge>
                        )}
                      </p>

                      {step.enabled && !step.quota_ok && (
                        <Alert color="warning" className="py-2 px-3 small">
                          {t("marketing.contactRuler.quota.stepBlocked")}
                        </Alert>
                      )}

                      <p className="small text-muted mb-3">{summaryLine(step)}</p>

                      {(step.key === "return" || step.key === "protocol_care") &&
                        Number(step.summary.sent_today ?? 0) > 0 && (
                          <Badge color="info" className="mb-3">
                            {t("marketing.contactRuler.return.sentToday", {
                              n: Number(step.summary.sent_today ?? 0),
                            })}
                          </Badge>
                        )}

                      {step.key === "protocol_care" &&
                        step.enabled &&
                        activeProtocolsCount === 0 && (
                          <Alert color="light" className="border py-2 px-3 small mb-3">
                            {t("marketing.contactRuler.protocolCare.emptyProtocols")}{" "}
                            <Link to="/clients">
                              {t("marketing.contactRuler.protocolCare.emptyProtocolsCta")}
                            </Link>
                          </Alert>
                        )}

                      {step.key === "birthday" && missingBirthDates && (
                        <Alert color="light" className="border py-2 px-3 small mb-3">
                          {withoutBirthCount > 0
                            ? t("marketing.contactRuler.birthday.missingBirthDates", {
                                count: withoutBirthCount,
                              })
                            : t("marketing.contactRuler.birthday.emptyBirthDates")}{" "}
                          <Link to="/clients?missing_birth_date=1&hint=birthday">
                            {t("marketing.contactRuler.birthday.emptyBirthDatesCta")}
                          </Link>
                        </Alert>
                      )}

                      {step.key === "birthday" && (
                        <FormGroup className="mb-3">
                          <Label for="birthday-lead-days" className="form-label">
                            {t("marketing.contactRuler.birthdayLeadDays")}
                          </Label>
                          <div className="d-flex gap-2">
                            <Input
                              id="birthday-lead-days"
                              type="number"
                              min={0}
                              max={14}
                              value={birthdayLeadDays}
                              onChange={(e) =>
                                setBirthdayLeadDays(
                                  Math.max(0, Math.min(14, Number(e.target.value) || 0))
                                )
                              }
                            />
                            <Button
                              color="light"
                              disabled={savingKey === "birthday_lead"}
                              onClick={() => void onSaveBirthdayLead()}
                            >
                              {savingKey === "birthday_lead" ? (
                                <Spinner size="sm" />
                              ) : (
                                t("marketing.contactRuler.saveLead")
                              )}
                            </Button>
                          </div>
                        </FormGroup>
                      )}

                      {step.key === "return" && (
                        <FormGroup className="mb-3">
                          <Label for="return-lead-days" className="form-label">
                            {t("marketing.contactRuler.returnLeadDays")}
                          </Label>
                          <div className="d-flex gap-2">
                            <Input
                              id="return-lead-days"
                              type="number"
                              min={0}
                              max={14}
                              value={returnLeadDays}
                              onChange={(e) =>
                                setReturnLeadDays(
                                  Math.max(0, Math.min(14, Number(e.target.value) || 0))
                                )
                              }
                            />
                            <Button
                              color="light"
                              disabled={savingKey === "return_lead"}
                              onClick={() => void onSaveReturnLead()}
                            >
                              {savingKey === "return_lead" ? (
                                <Spinner size="sm" />
                              ) : (
                                t("marketing.contactRuler.saveLead")
                              )}
                            </Button>
                          </div>
                        </FormGroup>
                      )}

                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          color={step.enabled ? "soft-danger" : "soft-success"}
                          size="sm"
                          disabled={savingKey === step.key}
                          onClick={() => void onToggle(step)}
                        >
                          {savingKey === step.key ? (
                            <Spinner size="sm" />
                          ) : step.enabled ? (
                            t("marketing.contactRuler.actions.turnOff")
                          ) : (
                            t("marketing.contactRuler.actions.turnOn")
                          )}
                        </Button>
                        <Button
                          tag={Link}
                          to={step.config_path}
                          color="soft-primary"
                          size="sm"
                        >
                          {t("marketing.contactRuler.actions.configure")}
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <Card className="mt-4 shadow-none border">
              <CardBody>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <div>
                    <h5 className="card-title mb-1">
                      {t("marketing.contactRuler.returnDue.title")}
                    </h5>
                    <p className="text-muted small mb-0">
                      {t("marketing.contactRuler.returnDue.subtitle")}
                    </p>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      color="light"
                      size="sm"
                      disabled={loadingDue}
                      onClick={() => void loadDue(duePage)}
                    >
                      {loadingDue ? (
                        <Spinner size="sm" />
                      ) : (
                        t("marketing.contactRuler.returnDue.refresh")
                      )}
                    </Button>
                    <Button
                      color="soft-primary"
                      size="sm"
                      disabled={exportingDue || (dueMeta?.total ?? 0) === 0}
                      onClick={() => void onExportDue()}
                    >
                      {exportingDue ? (
                        <Spinner size="sm" />
                      ) : (
                        t("marketing.contactRuler.returnDue.export")
                      )}
                    </Button>
                  </div>
                </div>

                {loadingDue ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : dueItems.length === 0 ? (
                  <div className="text-muted">
                    <p className="mb-2">{t("marketing.contactRuler.returnDue.empty")}</p>
                    <p className="small mb-3">
                      {t("marketing.contactRuler.returnDue.emptyHint")}
                    </p>
                    <Button tag={Link} to="/services" color="soft-primary" size="sm">
                      {t("marketing.contactRuler.returnDue.emptyCta")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table hover className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("marketing.contactRuler.returnDue.columns.client")}</th>
                            <th>{t("marketing.contactRuler.returnDue.columns.service")}</th>
                            <th>{t("marketing.contactRuler.returnDue.columns.dueOn")}</th>
                            <th className="text-end">
                              {t("marketing.contactRuler.returnDue.columns.actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {dueItems.map((item) => (
                            <tr
                              key={`${item.client_id}-${item.service_id}-${item.reference_appointment_id}`}
                            >
                              <td>
                                <Link to={`/clients/${item.client_id}`} className="text-body">
                                  {item.client_name}
                                </Link>
                                <Badge color="light" className="ms-2 text-muted border">
                                  {t("marketing.contactRuler.returnDue.abVariant", {
                                    variant: abVariantForClient(item.client_id),
                                  })}
                                </Badge>
                              </td>
                              <td>{item.service_name}</td>
                              <td>{formatDueDate(item.due_on)}</td>
                              <td className="text-end">
                                <Button
                                  tag={Link}
                                  to={`/agenda?client_id=${item.client_id}&service_id=${item.service_id}${
                                    item.unit_id ? `&unit_id=${item.unit_id}` : ""
                                  }`}
                                  color="soft-success"
                                  size="sm"
                                >
                                  {t("marketing.contactRuler.returnDue.rebook")}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {dueTotalPages > 1 && (
                      <Pagination className="pagination-sm justify-content-end mt-3 mb-0">
                        {Array.from({ length: dueTotalPages }, (_, index) => index + 1).map(
                          (pageNumber) => (
                            <PaginationItem
                              key={pageNumber}
                              active={pageNumber === duePage}
                            >
                              <PaginationLink
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  void loadDue(pageNumber);
                                }}
                              >
                                {pageNumber}
                              </PaginationLink>
                            </PaginationItem>
                          )
                        )}
                      </Pagination>
                    )}
                  </>
                )}
              </CardBody>
            </Card>

            <Row className="g-3 mt-1">
              <Col lg={4}>
                <Card className="shadow-none border h-100">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">
                        {t("marketing.contactRuler.preview.title")}
                      </h5>
                      <Button
                        color="light"
                        size="sm"
                        disabled={loadingBirthdayPreview}
                        onClick={() => void loadBirthdayPreview()}
                      >
                        {loadingBirthdayPreview ? (
                          <Spinner size="sm" />
                        ) : (
                          t("marketing.contactRuler.preview.refresh")
                        )}
                      </Button>
                    </div>
                    {birthdayPreview ? (
                      <>
                        <p className="small mb-1">
                          <strong>{t("marketing.contactRuler.preview.subjectA")}:</strong>{" "}
                          {birthdayPreview.subject}
                        </p>
                        {birthdayPreview.subject_b && (
                          <p className="small mb-1">
                            <strong>{t("marketing.contactRuler.preview.subjectB")}:</strong>{" "}
                            {birthdayPreview.subject_b}
                          </p>
                        )}
                        {birthdayPreview.ab_hint && (
                          <p className="text-muted small mb-2">{birthdayPreview.ab_hint}</p>
                        )}
                        <div
                          className="border rounded p-3 bg-light"
                          // Preview HTML is generated by our own Blade template (no user HTML).
                          dangerouslySetInnerHTML={{ __html: birthdayPreview.html }}
                        />
                      </>
                    ) : (
                      <p className="text-muted mb-0">
                        {t("marketing.contactRuler.preview.empty")}
                      </p>
                    )}
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="shadow-none border h-100">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">
                        {t("marketing.contactRuler.returnPreview.title")}
                      </h5>
                      <Button
                        color="light"
                        size="sm"
                        disabled={loadingReturnPreview}
                        onClick={() => void loadReturnPreview()}
                      >
                        {loadingReturnPreview ? (
                          <Spinner size="sm" />
                        ) : (
                          t("marketing.contactRuler.returnPreview.refresh")
                        )}
                      </Button>
                    </div>
                    {returnPreview ? (
                      <>
                        <p className="small mb-1">
                          <strong>{t("marketing.contactRuler.preview.subjectA")}:</strong>{" "}
                          {returnPreview.subject}
                        </p>
                        {returnPreview.subject_b && (
                          <p className="small mb-1">
                            <strong>{t("marketing.contactRuler.preview.subjectB")}:</strong>{" "}
                            {returnPreview.subject_b}
                          </p>
                        )}
                        {returnPreview.ab_hint && (
                          <p className="text-muted small mb-2">{returnPreview.ab_hint}</p>
                        )}
                        <div
                          className="border rounded p-3 bg-light"
                          dangerouslySetInnerHTML={{ __html: returnPreview.html }}
                        />
                      </>
                    ) : (
                      <p className="text-muted mb-0">
                        {t("marketing.contactRuler.returnPreview.empty")}
                      </p>
                    )}
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="shadow-none border h-100">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">
                        {t("marketing.contactRuler.protocolCarePreview.title")}
                      </h5>
                      <Button
                        color="light"
                        size="sm"
                        disabled={loadingProtocolCarePreview}
                        onClick={() => void loadProtocolCarePreview()}
                      >
                        {loadingProtocolCarePreview ? (
                          <Spinner size="sm" />
                        ) : (
                          t("marketing.contactRuler.protocolCarePreview.refresh")
                        )}
                      </Button>
                    </div>
                    {protocolCarePreview ? (
                      <>
                        <p className="small mb-1">
                          <strong>{t("marketing.contactRuler.preview.subjectA")}:</strong>{" "}
                          {protocolCarePreview.subject}
                        </p>
                        {protocolCarePreview.subject_b && (
                          <p className="small mb-1">
                            <strong>{t("marketing.contactRuler.preview.subjectB")}:</strong>{" "}
                            {protocolCarePreview.subject_b}
                          </p>
                        )}
                        {protocolCarePreview.ab_hint && (
                          <p className="text-muted small mb-2">
                            {protocolCarePreview.ab_hint}
                          </p>
                        )}
                        <div
                          className="border rounded p-3 bg-light"
                          dangerouslySetInnerHTML={{
                            __html: protocolCarePreview.html,
                          }}
                        />
                      </>
                    ) : (
                      <p className="text-muted mb-0">
                        {t("marketing.contactRuler.protocolCarePreview.empty")}
                      </p>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row className="g-3 mt-1">
              <Col lg={8}>
                <Card className="shadow-none border h-100">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <div>
                        <h5 className="card-title mb-1">
                          {t("marketing.contactRuler.loyaltyNearRewardPreview.title")}
                        </h5>
                        <p className="text-muted small mb-0">
                          {t("marketing.contactRuler.loyaltyNearRewardPreview.subtitle")}
                        </p>
                        <p className="text-muted small mb-0 mt-1">
                          {t(
                            "marketing.contactRuler.loyaltyNearRewardPreview.platformCopyHint"
                          )}
                        </p>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          tag={Link}
                          to="/loyalty-cards"
                          color="soft-primary"
                          size="sm"
                        >
                          {t("marketing.contactRuler.loyaltyNearRewardPreview.openCards")}
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          disabled={loadingLoyaltyNearRewardPreview}
                          onClick={() => void loadLoyaltyNearRewardPreview()}
                        >
                          {loadingLoyaltyNearRewardPreview ? (
                            <Spinner size="sm" />
                          ) : (
                            t("marketing.contactRuler.loyaltyNearRewardPreview.refresh")
                          )}
                        </Button>
                      </div>
                    </div>
                    {loyaltyNearRewardPreview ? (
                      <>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <Badge
                            color={
                              loyaltyNearRewardPreview.channels.whatsapp.eligible
                                ? "success"
                                : "secondary"
                            }
                          >
                            {loyaltyNearRewardPreview.channels.whatsapp.eligible
                              ? t(
                                  "marketing.contactRuler.loyaltyNearRewardPreview.waReady"
                                )
                              : t(
                                  "marketing.contactRuler.loyaltyNearRewardPreview.waBlocked"
                                )}
                          </Badge>
                          <Badge
                            color={
                              loyaltyNearRewardPreview.channels.email.eligible
                                ? "success"
                                : "secondary"
                            }
                          >
                            {loyaltyNearRewardPreview.channels.email.eligible
                              ? t(
                                  "marketing.contactRuler.loyaltyNearRewardPreview.emailReady"
                                )
                              : t(
                                  "marketing.contactRuler.loyaltyNearRewardPreview.emailBlocked"
                                )}
                          </Badge>
                          {!loyaltyNearRewardPreview.channels.whatsapp.quota_ok ? (
                            <Badge color="warning">
                              {t(
                                "marketing.contactRuler.loyaltyNearRewardPreview.waFreeHint"
                              )}
                            </Badge>
                          ) : null}
                        </div>
                        <p className="small mb-1">
                          <strong>
                            {t("marketing.contactRuler.loyaltyNearRewardPreview.waLabel")}:
                          </strong>{" "}
                          {loyaltyNearRewardPreview.whatsapp_body}
                        </p>
                        <p className="small mb-2">
                          <strong>{t("marketing.contactRuler.preview.subject")}:</strong>{" "}
                          {loyaltyNearRewardPreview.subject}
                        </p>
                        <div
                          className="border rounded p-3 bg-light"
                          dangerouslySetInnerHTML={{
                            __html: loyaltyNearRewardPreview.html,
                          }}
                        />
                      </>
                    ) : (
                      <p className="text-muted mb-0">
                        {t("marketing.contactRuler.loyaltyNearRewardPreview.empty")}
                      </p>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default ContactRulerPage;
